import { useState } from 'react';
import type { TicketDetailTimelineEntry, TicketEffortView, TicketRoutingView } from '@nightshift/ui-types';

import { Empty } from '../../components/primitives.tsx';
import { EffortSummary } from './effort.tsx';
import { useTimeFormat } from '../../lib/display.ts';
import { clockStamp, observedTime } from '../../lib/format.ts';
import { isKeyTimelineEntry } from '../../lib/timeline-filter.ts';
import './timeline.css';

const KIND_LABEL: Record<TicketDetailTimelineEntry['kind'], string> = {
  event: 'Event',
  attempt: 'Attempt',
  human: 'Human',
  checkpoint: 'Checkpoint',
};

/**
 * Chronological domain_events, plus attempts / human_actions / checkpoints that
 * are not already represented. Empty is honest — there is no sidecar history table.
 */
/**
 * Which route list the latest attempt used and why, beside the size the ticket was judged.
 * The timeline's attempt rows carry the same phrase for every earlier attempt.
 */
function RoutingSummary({ routing }: { routing: TicketRoutingView }) {
  const size = `${routing.tier} ticket${routing.tierSource === 'override' ? ', set on the ticket' : ''}`;
  return (
    <section className="timeline-routing" aria-label="Route choice">
      <span className="drawer-section-kicker">Route</span>
      <p className="timeline-routing-what">
        {routing.latest ? (
          <>
            <strong>{routing.latest.label}</strong>
            <span className="dim"> · {routing.latest.state}</span>
          </>
        ) : (
          <span className="dim">No attempt has been routed yet.</span>
        )}
      </p>
      <p className="dim timeline-routing-why">
        Size: {size} ({routing.tierDetail})
      </p>
    </section>
  );
}

export function TimelineTab({
  entries,
  now,
  effort,
  routing,
}: {
  entries: readonly TicketDetailTimelineEntry[];
  now: number;
  effort?: TicketEffortView;
  routing?: TicketRoutingView;
}) {
  const format = useTimeFormat();
  const [showAll, setShowAll] = useState(false);
  if (entries.length === 0) {
    return (
      <Empty
        title="No timeline yet."
        body="State changes, attempts and human actions appear here as they are recorded. There is no separate history table."
      />
    );
  }

  const visible = showAll ? entries : entries.filter(isKeyTimelineEntry);
  const hidden = entries.length - visible.length;

  return (
    <div className="timeline">
      {effort ? <EffortSummary effort={effort} now={now} /> : null}
      {routing ? <RoutingSummary routing={routing} /> : null}
      {hidden > 0 || showAll ? (
        <div className="timeline-filter">
          <span className="dim">
            {showAll ? 'Showing everything' : `Key events · ${hidden} routine ${hidden === 1 ? 'entry' : 'entries'} hidden`}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-pressed={showAll}
            onClick={() => setShowAll((value) => !value)}
          >
            {showAll ? 'Key events only' : 'Show all'}
          </button>
        </div>
      ) : null}
      <ol className="timeline-list">
        {visible.map((entry) => {
          // A policy approval stands apart from a person's: its own label and edge, so nobody
          // reads "Auto-approved" in a list of human actions and assumes someone looked.
          const policy = entry.eventType === 'PolicyApproved';
          return (
            <li key={entry.id} className={`timeline-row is-${entry.kind}${policy ? ' is-policy' : ''}`}>
              <time className="timeline-when num" dateTime={new Date(entry.at).toISOString()} title={clockStamp(entry.at)}>
                {observedTime(entry.at, now, format)}
              </time>
              <span className="timeline-kind">{policy ? 'Policy' : KIND_LABEL[entry.kind]}</span>
              <strong className="timeline-title">{entry.title}</strong>
              {entry.detail ? <p className="timeline-detail dim">{entry.detail}</p> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
